import { cloneDeep, isEqual } from "lodash";
import {
  IAxiosBlankReturn,
  IAxiosReturnFail,
  IAxiosReturnSuccess,
  IRequestStatus,
} from "./interface";

export type LoadFunctionWithData<T, R> = (
  backEndUrl: string,
  target: T,
  controller: AbortController,
  onProgress?: (progressPercent: number) => void,
) => Promise<IAxiosReturnFail | IAxiosReturnSuccess<R>>;

export type LoadFunctionBlank<T> = (
  backEndUrl: string,
  target: T,
  controller: AbortController,
  onProgress?: (progressPercent: number) => void,
) => Promise<IAxiosReturnFail | IAxiosBlankReturn>;

/**
 * Wrapper for API calls that controls request flow and updates status.
 * Ensures calls are only made when needed and gracefully handles cancellation and errors.
 *
 * When `loadFunction` returns data (`LoadFunctionWithData`), the resolved value is
 * either a successful axios return with typed data or a failure. When `loadFunction`
 * is blank (`LoadFunctionBlank`), the resolved value is either a blank success or a failure.
 *
 * @param loadFunction - API call function. Either a data-returning function (`LoadFunctionWithData`) or a blank-returning function (`LoadFunctionBlank`).
 * @param target - Target identifier or payload used for request de-duplication and tracking.
 * @param status - Current request status snapshot, used to decide whether to trigger a new request.
 * @param setStatus - Callback to update the request status throughout the call lifecycle.
 * @param backEndUrl - Base backend URL used by the `loadFunction`.
 * @param onProgress - Optional progress callback for upload operations.
 * @returns A promise resolving to either a successful axios return with data, a blank success, a failure, or null if no request was made.
 */
export function loadWrapper<T, R>(
  loadFunction: LoadFunctionWithData<T, R>,
  target: T,
  status: IRequestStatus<T>,
  setStatus: (ret: IRequestStatus<T>) => void,
  backEndUrl: string,
  onProgress?: (progressPercent: number) => void,
): Promise<IAxiosReturnSuccess<R> | IAxiosReturnFail | null>;

export function loadWrapper<T>(
  loadFunction: LoadFunctionBlank<T>,
  target: T,
  status: IRequestStatus<T>,
  setStatus: (ret: IRequestStatus<T>) => void,
  backEndUrl: string,
  onProgress?: (progressPercent: number) => void,
): Promise<IAxiosBlankReturn | null>;

export async function loadWrapper<T, R>(
  loadFunction: LoadFunctionWithData<T, R> | LoadFunctionBlank<T>,
  target: T,
  status: IRequestStatus<T>,
  setStatus: (ret: IRequestStatus<T>) => void,
  backEndUrl: string,
  onProgress?: (progressPercent: number) => void,
): Promise<IAxiosReturnFail | IAxiosBlankReturn | IAxiosReturnSuccess<R> | null> {
  // Assume nothing is happening
  const doLoad =
    (status.requesting && !isEqual(status.requestingId, target)) ||
    (!status.requesting && !isEqual(status.requestedId, target));

  // Check there is a need to request
  if (doLoad && status.cancel) {
    status.cancel();
  }

  // Stop if hierarchy is less than zero or already requesting, or already errored
  if (doLoad && !status.error) {
    const controller = new AbortController();
    setStatus({
      requesting: true,
      requestingId: cloneDeep(target),
      cancel: () => controller.abort(),
      error: false,
    });
    const response: IAxiosReturnFail | IAxiosBlankReturn | IAxiosReturnSuccess<R> =
      await loadFunction(backEndUrl, target, controller, onProgress);
    if (response.success) {
      setStatus({
        requesting: false,
        requestedId: cloneDeep(target),
        error: false,
      });
      return response;
    } else {
      console.warn(
        `LoadWrapper fail: ${response.errorText} from ${backEndUrl}/${JSON.stringify(target)}`,
      );
      // Do nothing on a cancellation error
      /* istanbul ignore else */
      if (!response.errorText?.toLocaleLowerCase().includes("request cancelled")) {
        setStatus({
          requesting: false,
          requestedId: cloneDeep(target),
          error: true,
          errorText: response.errorText,
        });
        return response;
      }
    }
  }
  return null;
}
